import { FormEvent, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import FloatingInput from "../compoment/ui/FloatingInput";
import Email from "../compoment/icon/Email";
import Close from "../compoment/icon/Close";
import { formatPrice } from "../constants";
import type { PriceBreakdown } from "../utils/museumFeeTax";

interface GuestEmailModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (email: string) => void | Promise<void>;
  isSubmitting?: boolean;
  initialEmail?: string;
  pricing?: PriceBreakdown | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GuestEmailModal = ({
  show,
  onHide,
  onSubmit,
  isSubmitting = false,
  initialEmail = "",
  pricing = null,
}: GuestEmailModalProps) => {
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setEmail(initialEmail || "");
      setError("");
    }
  }, [show, initialEmail]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError("Email is required.");
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    setError("");
    await onSubmit(trimmed);
  };

  return (
    <Modal
      show={show}
      onHide={isSubmitting ? undefined : onHide}
      backdrop="static"
      centered
      className="guestEmailModal"
      keyboard={!isSubmitting}
    >
      <Modal.Body>
        <button
          type="button"
          className="guestEmailClose"
          onClick={onHide}
          disabled={isSubmitting}
          aria-label="Close"
        >
          <Close />
        </button>

        <div className="guestEmailTitle">Almost there</div>
        <p className="guestEmailSubtitle">
          Enter your email so we can attach this order for pickup at reception.
        </p>

        {pricing && (
          <div className="guestEmailPriceBreakdown" aria-live="polite">
            <div className="guestEmailPriceBreakdownRow">
              <span>Subtotal</span>
              <span>{formatPrice(pricing.subtotal)}</span>
            </div>
            <div className="guestEmailPriceBreakdownRow">
              <span>Processing fee ({pricing.processingFeePercent}%)</span>
              <span>{formatPrice(pricing.processingFee)}</span>
            </div>
            <div className="guestEmailPriceBreakdownRow">
              <span>Sales tax ({pricing.salesTaxPercent}%)</span>
              <span>{formatPrice(pricing.salesTax)}</span>
            </div>
            <div className="guestEmailPriceBreakdownTotal">
              <span>Total</span>
              <strong>{formatPrice(pricing.total)} USD</strong>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="guestEmailForm">
          <FloatingInput
            placeholder="Email address"
            label="Email"
            type="email"
            name="guestEmail"
            value={email}
            autoComplete="email"
            icon={Email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError("");
            }}
          />
          {error && <div className="guestEmailError">{error}</div>}

          <button
            type="submit"
            className="guestEmailSubmit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Placing order…"
              : pricing
                ? `Confirm · ${formatPrice(pricing.total)}`
                : "Continue"}
          </button>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default GuestEmailModal;
